import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Select,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  FormControl,
  CircularProgress,
} from "@material-ui/core";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import {
  getUnmappedTeams,
  assignSportToTeam,
} from "../../crud/api/reconciliation.crud";
import { getAllActiveMasterSports } from "../../crud/api/sports.crud";
import * as reconciliationDUCK from "../../../redux/ducks/reconciliation.duck";
import * as sportDUCK from "../../../redux/ducks/sport.duck";

const Reconciliation = ({
  unmappedTeams,
  masterSports,
  reconciliationActions,
  sportActions,
}) => {
  const [selections, setSelections] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    getUnmappedTeams(reconciliationActions.setUnmappedTeams);
    // Picker variant: one-shot, unpaginated, active-only. The default
    // getMasterSports is paginated (per_page ~15), which would silently
    // hide sports past the first page from the dropdown.
    getAllActiveMasterSports(sportActions.setMasterSports);
  }, [reconciliationActions.setUnmappedTeams, sportActions.setMasterSports]);

  const handlePick = (teamId, sportId) => {
    setSelections((prev) => ({ ...prev, [teamId]: sportId }));
  };

  // Backend pages are 1-based, MUI TablePagination is 0-based.
  const handleChangePage = (_event, newPageZeroBased) => {
    getUnmappedTeams(reconciliationActions.setUnmappedTeams, newPageZeroBased + 1);
  };

  const handleSave = async (teamId, fallbackSportId) => {
    // Allow saving with the backend-suggested sport when the admin didn't
    // override it. Without the fallback, clicking Save on a row with a
    // suggestion but no explicit pick silently no-ops.
    const sportId = selections[teamId] || fallbackSportId;
    if (!sportId) return;
    setSavingId(teamId);
    try {
      await assignSportToTeam(reconciliationActions.resolveTeam, teamId, sportId);
      setSelections((prev) => {
        const next = { ...prev };
        delete next[teamId];
        return next;
      });
    } finally {
      setSavingId(null);
    }
  };

  const teams = unmappedTeams.data || [];
  const sports = masterSports.data || [];

  return (
    <div className="table-container">
      <div className="pb-4">
        <Typography variant="h6">Team Reconciliation</Typography>
        <Typography variant="caption" color="textSecondary">
          Zone Super Admin only. Transitional surface for TZ-784: assign a
          master sport to every existing team across the 14 active schools.
          When the list below is empty, reconciliation is complete and this
          page can be hidden.
        </Typography>
      </div>

      {unmappedTeams.isFetched && teams.length === 0 && (
        <Paper style={{ padding: 24 }}>
          <Typography>
            All teams reconciled. Nothing more to map.
          </Typography>
        </Paper>
      )}

      {teams.length > 0 && (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Team</TableCell>
                <TableCell>School</TableCell>
                <TableCell>Suggested</TableCell>
                <TableCell>Pick master sport</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teams.map((team) => {
                const initial =
                  selections[team.id] || team.suggested_sport_id || "";
                return (
                  <TableRow key={team.id} id={`unmapped_team_${team.id}`}>
                    <TableCell>{team.name}</TableCell>
                    <TableCell>{team.school_name}</TableCell>
                    <TableCell>
                      {team.suggested_sport_name || (
                        <span style={{ color: "#888" }}>— none —</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <Select
                          value={initial}
                          onChange={(e) => handlePick(team.id, e.target.value)}
                          displayEmpty
                          inputProps={{
                            id: `reconciliation_sport_${team.id}`,
                          }}
                        >
                          <MenuItem value="">
                            <em>Choose…</em>
                          </MenuItem>
                          {sports.map((sport) => (
                            <MenuItem
                              key={sport.id}
                              value={sport.id}
                              id={`reconciliation_sport_${team.id}_${sport.id}`}
                            >
                              {sport.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={
                          savingId === team.id ||
                          !(
                            selections[team.id] || team.suggested_sport_id
                          )
                        }
                        onClick={() =>
                          handleSave(team.id, team.suggested_sport_id)
                        }
                        id={`reconciliation_save_${team.id}`}
                      >
                        {savingId === team.id ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : (
                          "Save"
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {unmappedTeams.count > (unmappedTeams.rowsPerPage || 15) && (
            <TablePagination
              component="div"
              count={unmappedTeams.count || 0}
              page={Math.max(0, (unmappedTeams.page || 1) - 1)}
              rowsPerPage={unmappedTeams.rowsPerPage || 15}
              rowsPerPageOptions={[]}
              onChangePage={handleChangePage}
            />
          )}
        </Paper>
      )}
    </div>
  );
};

Reconciliation.propTypes = {
  unmappedTeams: PropTypes.shape({
    data: PropTypes.array,
    count: PropTypes.number,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    isFetched: PropTypes.bool,
  }).isRequired,
  masterSports: PropTypes.shape({
    data: PropTypes.array,
  }).isRequired,
  reconciliationActions: PropTypes.object.isRequired,
  sportActions: PropTypes.object.isRequired,
};

const mapStateToProps = (store) => ({
  unmappedTeams: store.reconciliation.unmapped_teams,
  masterSports: store.sport.master_sports,
});

const mapDispatchToProps = (dispatch) => ({
  reconciliationActions: bindActionCreators(
    reconciliationDUCK.actions,
    dispatch
  ),
  sportActions: bindActionCreators(sportDUCK.actions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Reconciliation);
